import { UserRoleEnum } from '../models/enums'
import type { FormListInterface } from '../models/types'
import type Insured from '../../common/common/models/APP/Insured'
import type FormPage from '@/modules/common/common/models/APP/FormPage'
import type { PetHiring } from '@/modules/pets/hiring/models/APP/PetHiring'
import type { RouteLocationRaw, Router } from 'vue-router'

export function useNavigation() {
  function getMCodeQuery(router: Router) {
    const mCode = router.currentRoute.value.query.mCode
    return mCode ? { mCode } : {}
  }

  function toRouteWithMCode(router: Router, to: RouteLocationRaw): RouteLocationRaw {
    if (typeof to === 'string') {
      return to
    }

    return {
      ...to,
      query: {
        ...getMCodeQuery(router),
        ...(to.query ?? {})
      }
    }
  }

  function pushPreservingMCode(router: Router, to: RouteLocationRaw) {
    return router.push(toRouteWithMCode(router, to))
  }

  function replacePreservingMCode(router: Router, to: RouteLocationRaw) {
    return router.replace(toRouteWithMCode(router, to))
  }

  function findPagesByStepProcess(
    pages: FormListInterface | Record<string, Record<string, FormPage>>,
    stepProcess?: number
  ) {
    if (!stepProcess) return null

    const pageNames: string[] = []
    Object.entries(pages).forEach(([key, values]) => {
      if (values.extra?.processStep?.includes(stepProcess)) {
        pageNames.push(key)
      }
    })

    return pageNames
  }

  function findIncompleteInsured(insureds: Insured[]) {
    const insuredsFiltered = insureds.filter((i) => i.role !== UserRoleEnum.HOLDER_INSURED)
    const index = insuredsFiltered.findIndex((insured) => !insured.documentDetails.documentNumber)

    return Math.max(index + 1, 1)
  }

  function findIncompletePet(pets: PetHiring[]) {
    const index = pets.findIndex((pet) => !pet.chipNumber)

    return index > -1 ? index : 0
  }

  function getPageByStepProcess<I extends Insured = Insured>(
    pages: FormListInterface | Record<string, Record<string, FormPage>>,
    stepProcess: number | null,
    insureds: I[],
    insuredsStep: number
  ) {
    try {
      if (!stepProcess) {
        throw new Error('Step process is required')
      }

      const pageNames = findPagesByStepProcess(pages, stepProcess)
      if (!pageNames) {
        throw new Error('No pages found for the given step process')
      }

      if (stepProcess === insuredsStep) {
        const index = findIncompleteInsured(insureds)

        return pageNames[index]
      }

      return pageNames[0]
    } catch (error) {
      return Object.keys(pages)[0]
    }
  }

  function getPageByStepProcessPets<I extends PetHiring = PetHiring>(
    pages: FormListInterface | Record<string, Record<string, FormPage>>,
    stepProcess: number | null,
    pets: I[],
    insuredsStep: number
  ) {
    try {
      if (!stepProcess) {
        throw new Error('Step process is required')
      }

      const pageNames = findPagesByStepProcess(pages, stepProcess)
      if (!pageNames) {
        throw new Error('No pages found for the given step process')
      }

      if (stepProcess === insuredsStep) {
        const index = findIncompletePet(pets)

        return pageNames[index]
      }

      return pageNames[0]
    } catch (error) {
      return Object.keys(pages)[0]
    }
  }

  return {
    getPageByStepProcess,
    getPageByStepProcessPets,
    pushPreservingMCode,
    replacePreservingMCode
  }
}
